import { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import SelectorNegocio from '../../components/SelectorNegocio'

// 'pending_payment' va primero porque es donde nacen las encomiendas y es el
// estado que exige una acción: mientras nadie confirme el pago, no sale nada.
const STATUSES = ['pending_payment', 'pending', 'confirmed', 'shipped', 'delivered', 'cancelled']
const STATUS_LABEL = {
  pending_payment: 'Pendiente de pago',
  pending: 'Pendiente', confirmed: 'Confirmado', shipped: 'Enviado',
  delivered: 'Entregado', cancelled: 'Cancelado',
}
const STATUS_COLOR = {
  pending_payment: { background: 'rgba(239,68,68,0.12)',  color: '#b91c1c' },
  pending:   { background: 'rgba(245,158,11,0.15)', color: '#d97706' },
  confirmed: { background: 'rgba(59,130,246,0.15)', color: '#2563eb' },
  shipped:   { background: 'rgba(168,85,247,0.15)', color: '#9333ea' },
  delivered: { background: 'rgba(16,185,129,0.15)', color: '#059669' },
  cancelled: { background: 'rgba(239,68,68,0.15)',  color: '#dc2626' },
}

const ENTREGA_LABEL = {
  delivery: 'A domicilio',
  punto: 'Punto de entrega',
  encomienda: 'Encomienda',
}

function fmtMoney(n) {
  const v = Number(n) || 0
  return v.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Un dato del pedido que se puede corregir en el sitio.
 *
 * Se edita al hacer clic y se guarda al salir del campo o con Enter — sin un
 * modo "editar todo" ni un botón de guardar global: casi siempre se corrige un
 * solo dato (un dígito del celular, el nombre de la agencia) y obligar a entrar
 * en un formulario completo para eso hace que nadie lo corrija.
 *
 * Solo llama a onGuardar si el valor cambió de verdad.
 */
function CampoEditable({ etiqueta, valor, onGuardar, ancho = false }) {
  const [editando, setEditando] = useState(false)
  const [borrador, setBorrador] = useState(valor ?? '')

  useEffect(() => { setBorrador(valor ?? '') }, [valor])

  const cerrar = () => {
    setEditando(false)
    const limpio = String(borrador ?? '').trim()
    if (limpio !== String(valor ?? '').trim()) onGuardar(limpio)
  }

  return (
    <div style={ancho ? { gridColumn: '1 / -1' } : undefined}>
      <p className="text-muted" style={{ fontSize: 11 }}>{etiqueta}</p>
      {editando ? (
        <input
          autoFocus
          value={borrador}
          onChange={e => setBorrador(e.target.value)}
          onBlur={cerrar}
          onKeyDown={e => {
            if (e.key === 'Enter') cerrar()
            if (e.key === 'Escape') { setBorrador(valor ?? ''); setEditando(false) }
          }}
          style={{ width: '100%', padding: '4px 6px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)' }}
        />
      ) : (
        <p onClick={() => setEditando(true)}
           title="Clic para editar"
           style={{ cursor: 'text', minHeight: 20, borderBottom: '1px dashed transparent' }}
           onMouseEnter={e => { e.currentTarget.style.borderBottomColor = 'var(--border)' }}
           onMouseLeave={e => { e.currentTarget.style.borderBottomColor = 'transparent' }}>
          {valor || <span className="text-muted">—</span>}
        </p>
      )}
    </div>
  )
}

export default function Pedidos() {
  const [pedidos, setPedidos] = useState([])
  const [tiendas, setTiendas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [view, setView] = useState(null)
  const [updating, setUpdating] = useState(null)
  const [negocio, setNegocio] = useState('todos')

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/pedidos', { params: { unidadNegocioId: negocio } })
      setPedidos(data.pedidos || [])
      setTiendas(data.tiendas || [])
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error cargando pedidos')
    } finally {
      setLoading(false)
    }
  }

  // Recarga al cambiar de negocio: cada marca ve solo sus tiendas
  useEffect(() => { load() }, [negocio])

  const filtered = useMemo(
    () => (filter === 'all' ? pedidos : pedidos.filter(p => p.status === filter)),
    [pedidos, filter]
  )

  const totalIngresos = useMemo(
    () => pedidos.filter(p => p.status !== 'cancelled').reduce((s, p) => s + Number(p.total || 0), 0),
    [pedidos]
  )

  /**
   * Un pedido confirmado SIN `external_sale_id` es uno cuya venta no llegó a
   * registrarse en el inventario: el stock nunca se descontó. Pasa si la
   * sincronización falla al confirmarlo.
   *
   * No se podía arreglar desde aquí: el estado se elige con un <select>, y
   * volver a marcar "Confirmado" no dispara onChange, así que el pedido se
   * quedaba así para siempre y el inventario divergía en silencio. Este botón
   * reenvía la confirmación, que es lo que vuelve a intentar la venta.
   */
  const reintentarSync = async (pedido) => {
    setUpdating(pedido.id)
    try {
      const { data } = await api.patch(`/pedidos/${pedido._sesionId}/${pedido.id}`, { status: 'confirmed' })
      setPedidos(prev => prev.map(p => (p.id === pedido.id ? { ...p, ...data.pedido } : p)))
      if (data.sync?.ok) toast.success('Venta registrada y stock descontado')
      else toast.error(`Sigue sin registrarse: ${data.sync?.error || 'la tienda no respondió'}`, { duration: 7000 })
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo reintentar')
    } finally { setUpdating(null) }
  }

  const borrar = async (pedido) => {
    if (!window.confirm(
      `¿Eliminar el pedido de ${pedido.customer_name}?

` +
      'Se borra de la tienda, no solo del CRM. Si el cliente se arrepintió, marcarlo como ' +
      '"Cancelado" es mejor: deja el registro de lo que pasó y devuelve el stock.'
    )) return
    try {
      await api.delete(`/pedidos/${pedido._sesionId}/${pedido.id}`)
      setPedidos(prev => prev.filter(p => p.id !== pedido.id))
      if (view?.id === pedido.id) setView(null)
      toast.success('Pedido eliminado')
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo eliminar el pedido', { duration: 7000 })
    }
  }

  /**
   * Confirmar a mano que el pago llegó.
   *
   * Es el único paso que desbloquea una encomienda: hasta que alguien mira el
   * comprobante, el bot le dice al cliente que está en verificación y no
   * promete el despacho. Se pide la referencia porque después nadie recuerda a
   * cuál de cinco Yapes del día correspondía este pedido.
   */
  const confirmarPago = async (pedido) => {
    const referencia = window.prompt(
      `Confirmar el pago de ${pedido.customer_name} — ${fmtMoney(pedido.total)}\n\n` +
      'Referencia del pago (opcional): últimos dígitos, hora del Yape, lo que te sirva para ubicarlo después.',
      ''
    )
    if (referencia === null) return
    setUpdating(pedido.id)
    try {
      const { data } = await api.patch(`/pedidos/${pedido._sesionId}/${pedido.id}`, {
        payment_status: 'confirmado',
        ...(referencia.trim() ? { payment_reference: referencia.trim() } : {}),
      })
      setPedidos(prev => prev.map(p => (p.id === pedido.id ? { ...p, ...data.pedido } : p)))
      if (view?.id === pedido.id) setView(v => ({ ...v, ...data.pedido }))
      toast.success('Pago confirmado — ya puedes despachar')
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo confirmar el pago')
    } finally {
      setUpdating(null)
    }
  }

  /**
   * Fija la hora de entrega.
   *
   * Texto libre a propósito: en la práctica se acuerda "mañana en la tarde" o
   * "hoy 6pm", no un instante exacto, y un selector de fecha y hora obliga a
   * inventar una precisión que nadie tiene. El bot no promete ninguna hora
   * hasta que aquí haya una.
   */
  const fijarHora = async (pedido) => {
    const hora = window.prompt(
      `Hora de entrega para ${pedido.customer_name}\n\n` +
      'Escríbelo como se lo vas a decir: "hoy 6pm", "mañana entre 3 y 5", "el sábado en la mañana".',
      pedido.delivery_slot || ''
    )
    if (hora === null) return
    try {
      const { data } = await api.patch(`/pedidos/${pedido._sesionId}/${pedido.id}`, {
        delivery_slot: hora.trim() || null,
      })
      setPedidos(prev => prev.map(p => (p.id === pedido.id ? { ...p, ...data.pedido } : p)))
      if (view?.id === pedido.id) setView(v => ({ ...v, ...data.pedido }))
      toast.success(hora.trim() ? 'Hora de entrega guardada' : 'Hora de entrega borrada')
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo guardar la hora')
    }
  }

  /** Corrige un dato del pedido en la tienda. */
  const editarCampo = async (pedido, campo, valor) => {
    try {
      const { data } = await api.patch(`/pedidos/${pedido._sesionId}/${pedido.id}`, { [campo]: valor })
      setPedidos(prev => prev.map(p => (p.id === pedido.id ? { ...p, ...data.pedido } : p)))
      if (view?.id === pedido.id) setView(v => ({ ...v, ...data.pedido }))
      toast.success('Guardado')
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo guardar el cambio')
    }
  }

  const updateStatus = async (pedido, status) => {
    setUpdating(pedido.id)
    try {
      const { data } = await api.patch(`/pedidos/${pedido._sesionId}/${pedido.id}`, { status })
      toast.success('Estado actualizado')
      setPedidos(prev => prev.map(p => (p.id === pedido.id ? { ...p, ...data.pedido } : p)))
      if (view?.id === pedido.id) setView(v => ({ ...v, ...data.pedido }))
      if (data.sync && !data.sync.ok) toast.error(`No se sincronizó con la tienda: ${data.sync.error}`)
      else if (status === 'confirmed' && data.sync?.ok) toast.success('Venta registrada y stock descontado')
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error actualizando el pedido')
    } finally {
      setUpdating(null)
    }
  }

  if (tiendas.length === 0 && !loading) {
    return (
      <div className="page">
        <h1 className="text-2xl font-bold" style={{ marginBottom: 18 }}>🛒 Pedidos</h1>
        <div className="card">
          <p className="text-muted">
            Ningún número tiene configurados los pedidos todavía. Ve a un número → Configurar bot →
            pestaña "🛒 Pedidos y Pagos" y activa "El bot puede tomar pedidos" con la URL/secreto de la tienda.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <header style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 className="text-2xl font-bold">🛒 Pedidos</h1>
          <p className="text-muted">
            {pedidos.length} pedidos · Ingresos: Bs {fmtMoney(totalIngresos)}
            {tiendas.length > 1 && ` · ${tiendas.length} tiendas conectadas`}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Actualizando...' : '🔄 Actualizar'}
        </button>
      </header>

      <div style={{ marginBottom: 16 }}>
        <SelectorNegocio valor={negocio} onChange={setNegocio} clave="pedidos" rubro="productos" />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setFilter('all')}
          className="btn"
          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, ...(filter === 'all' ? { background: 'var(--primary)', color: '#fff' } : { background: 'var(--bg-secondary)' }) }}
        >
          Todos ({pedidos.length})
        </button>
        {STATUSES.map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className="btn"
            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, ...(filter === s ? { background: 'var(--primary)', color: '#fff' } : { background: 'var(--bg-secondary)' }) }}
          >
            {STATUS_LABEL[s]} ({pedidos.filter(p => p.status === s).length})
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: 10 }}>Cliente</th>
              {tiendas.length > 1 && <th style={{ padding: 10 }}>Tienda</th>}
              <th style={{ padding: 10 }}>Fecha</th>
              <th style={{ padding: 10 }}>Items</th>
              <th style={{ padding: 10 }}>Total</th>
              <th style={{ padding: 10 }}>Estado</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Cargando...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Sin pedidos</td></tr>
            ) : filtered.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 10 }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {p.customer_name}
                    {p.origen === 'whatsapp' && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: 'rgba(16,185,129,0.15)', color: '#059669' }}>
                        📱 WhatsApp
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.phone}</div>
                  {p.tracking_code && <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>🔖 {p.tracking_code}</div>}
                  {p.status === 'confirmed' && !p.external_sale_id && (
                    <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: 'rgba(245,158,11,0.18)', color: '#b45309' }}>
                        ⚠️ sin descontar del inventario
                      </span>
                      <button className="btn" disabled={updating === p.id}
                        style={{ padding: '2px 8px', fontSize: 11 }}
                        onClick={() => reintentarSync(p)}>
                        {updating === p.id ? '…' : 'Reintentar'}
                      </button>
                    </div>
                  )}
                </td>
                {tiendas.length > 1 && <td style={{ padding: 10, fontSize: 12 }}>{p._tienda}</td>}
                <td style={{ padding: 10, fontSize: 12, color: 'var(--text-muted)' }}>
                  {new Date(p.created_at).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
                <td style={{ padding: 10, fontSize: 12 }}>
                  {p.items?.length || 0} producto(s)
                  {p.delivery_method && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {ENTREGA_LABEL[p.delivery_method] || p.delivery_method}
                      {p.delivery_point ? ` · ${p.delivery_point}` : ''}
                      {Number(p.shipping_cost) > 0 ? ` · envío Bs ${fmtMoney(p.shipping_cost)}` : ''}
                    </div>
                  )}
                </td>
                <td style={{ padding: 10, fontWeight: 600 }}>Bs {fmtMoney(p.total)}</td>
                <td style={{ padding: 10 }}>
                  <select
                    value={p.status}
                    disabled={updating === p.id}
                    onChange={e => updateStatus(p, e.target.value)}
                    style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 6, border: 'none', ...STATUS_COLOR[p.status] }}
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                  {/* El pago se muestra siempre que esté pendiente, no solo en
                      pending_payment: un pedido de Potosí también puede haberse
                      pagado por QR y conviene poder dejarlo registrado. */}
                  {p.payment_status === 'confirmado' ? (
                    <div style={{ marginTop: 4, fontSize: 10, fontWeight: 700, color: '#059669' }}
                         title={[p.payment_confirmed_by && `Confirmó: ${p.payment_confirmed_by}`,
                                 p.payment_reference && `Ref: ${p.payment_reference}`].filter(Boolean).join('\n')}>
                      ✅ pago confirmado
                    </div>
                  ) : (
                    <button className="btn" disabled={updating === p.id}
                      style={{ marginTop: 4, padding: '2px 8px', fontSize: 11, display: 'block' }}
                      onClick={() => confirmarPago(p)}>
                      {updating === p.id ? '…' : '💰 Confirmar pago'}
                    </button>
                  )}
                </td>
                <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setView(p)}>Ver</button>
                  <button className="btn" title="Eliminar pedido"
                    style={{ padding: '4px 8px', fontSize: 12, marginLeft: 6, color: '#dc2626', borderColor: '#fca5a5' }}
                    onClick={() => borrar(p)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {view && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setView(null)}
        >
          <div className="card" style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h2 className="text-xl font-bold">Pedido</h2>
                <p className="text-muted" style={{ fontSize: 12 }}>{new Date(view.created_at).toLocaleString('es-BO')}</p>
              </div>
              <button className="btn btn-secondary" onClick={() => setView(null)}>✕</button>
            </div>

            {/* Los datos de entrega se editan aquí porque casi siempre llegan
                dictados por WhatsApp: un número mal oído o una dirección a
                medias es lo normal, y corregirlo no debería obligar a entrar a
                la tienda. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, marginBottom: 14 }}>
              <CampoEditable etiqueta="Cliente"  valor={view.customer_name} onGuardar={v => editarCampo(view, 'customer_name', v)} />
              <CampoEditable etiqueta="Teléfono" valor={view.phone}         onGuardar={v => editarCampo(view, 'phone', v)} />
              <CampoEditable etiqueta="Ciudad"   valor={view.city}          onGuardar={v => editarCampo(view, 'city', v)} />
              <div>
                <p className="text-muted" style={{ fontSize: 11 }}>Entrega</p>
                <p>{ENTREGA_LABEL[view.delivery_method] || view.delivery_method}
                  {Number(view.shipping_cost) > 0 && <span className="text-muted"> · envío Bs {fmtMoney(view.shipping_cost)}</span>}
                </p>
              </div>
              {view.delivery_method === 'punto' && (
                <CampoEditable ancho etiqueta="Punto de entrega" valor={view.delivery_point} onGuardar={v => editarCampo(view, 'delivery_point', v)} />
              )}
              <CampoEditable ancho valor={view.address} onGuardar={v => editarCampo(view, 'address', v)}
                             etiqueta={view.delivery_method === 'encomienda' ? 'Agencia / dirección de destino' : 'Dirección'} />
              <CampoEditable ancho etiqueta="Referencia del lugar" valor={view.reference} onGuardar={v => editarCampo(view, 'reference', v)} />
              <CampoEditable ancho etiqueta="Notas" valor={view.notes} onGuardar={v => editarCampo(view, 'notes', v)} />
              <CampoEditable etiqueta="Código de seguimiento" valor={view.tracking_code} onGuardar={v => editarCampo(view, 'tracking_code', v)} />
              {tiendas.length > 1 && <div><p className="text-muted" style={{ fontSize: 11 }}>Tienda</p><p>{view._tienda}</p></div>}
            </div>

            {/* La ubicación compartida es lo que lleva al repartidor a la
                puerta: la dirección dictada por WhatsApp casi nunca alcanza. */}
            {view.delivery_lat && view.delivery_lng && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                <p className="text-muted" style={{ fontSize: 11, marginBottom: 4 }}>Ubicación compartida</p>
                <a href={`https://www.google.com/maps?q=${view.delivery_lat},${view.delivery_lng}`}
                   target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
                  📍 Abrir en Google Maps
                </a>
                {view.delivery_address_extra && (
                  <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>{view.delivery_address_extra}</p>
                )}
              </div>
            )}

            {view.delivery_method !== 'encomienda' && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                <p className="text-muted" style={{ fontSize: 11, marginBottom: 4 }}>Hora de entrega</p>
                {view.customer_slot && (
                  <p className="text-muted" style={{ fontSize: 12, marginBottom: 6 }}>
                    El cliente dijo que puede <strong>{view.customer_slot}</strong> — es su propuesta, no se le confirmó nada.
                  </p>
                )}
                {view.delivery_slot ? (
                  <p style={{ fontWeight: 600 }}>
                    🕒 {view.delivery_slot}
                    {view.delivery_confirmed_by && (
                      <span className="text-muted" style={{ fontWeight: 400, fontSize: 12 }}> · la fijó {view.delivery_confirmed_by}</span>
                    )}
                  </p>
                ) : (
                  <p className="text-muted" style={{ fontSize: 12 }}>
                    Sin acordar — el bot no le promete ninguna hora hasta que la pongas aquí.
                  </p>
                )}
                <button className="btn" style={{ marginTop: 6, fontSize: 12 }} onClick={() => fijarHora(view)}>
                  {view.delivery_slot ? 'Cambiar hora' : '🕒 Fijar hora de entrega'}
                </button>
              </div>
            )}

            <div style={{
              border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 12,
              background: view.payment_status === 'confirmado' ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.06)',
            }}>
              <p className="text-muted" style={{ fontSize: 11, marginBottom: 4 }}>Pago</p>
              {view.payment_status === 'confirmado' ? (
                <p style={{ fontWeight: 600, color: '#059669' }}>
                  ✅ Confirmado{view.payment_confirmed_by ? ` por ${view.payment_confirmed_by}` : ''}
                  {view.payment_reference ? <span className="text-muted" style={{ fontWeight: 400 }}> · {view.payment_reference}</span> : null}
                </p>
              ) : (
                <>
                  <p style={{ fontWeight: 600, color: '#b91c1c', marginBottom: 6 }}>
                    Pendiente{view.delivery_method === 'encomienda' ? ' — la encomienda no sale hasta confirmarlo' : ''}
                  </p>
                  <button className="btn" disabled={updating === view.id} onClick={() => confirmarPago(view)}>
                    💰 Confirmar pago
                  </button>
                </>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginBottom: 10 }}>
              <p style={{ fontWeight: 600, marginBottom: 6 }}>Productos</p>
              {(view.items || []).map((it, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                  {/* El color/talla elegido tiene que verse aquí: es lo que hay
                      que meter en la caja, y sin esto el pedido dice "2
                      unidades" y toca abrir la conversación para saber de cuál. */}
                  <span>
                    {it.quantity} × {it.name}
                    {it.opciones && Object.keys(it.opciones).length ? (
                      <span style={{ fontWeight: 600 }}> · {Object.entries(it.opciones).map(([k, v]) => `${k}: ${v}`).join(', ')}</span>
                    ) : null}
                    {it.sku ? <span className="text-muted" style={{ fontSize: 11 }}> ({it.sku})</span> : null}
                  </span>
                  <span style={{ fontWeight: 600 }}>Bs {fmtMoney(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginBottom: 12 }}>
              <span>Total</span>
              <span>Bs {fmtMoney(view.total)}</span>
            </div>

            {view.external_sale_id ? (
              <p style={{ fontSize: 12, color: '#059669' }}>✓ Sincronizado con la tienda (stock ya descontado)</p>
            ) : (
              <>
                {view.external_sale_error && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 6 }}>⚠ {view.external_sale_error}</p>}
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cambia el estado a "Confirmado" para registrar la venta y descontar stock.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
